import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { isSinger, bpmToTempoLabel } from '../utils/singerFriendly';
import {
  Music,
  ListMusic, 
  Heart, 
  Sparkles, 
  Users, 
  TrendingUp,
  ArrowRight,
  Play
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const singerMode = isSinger(user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/stats/dashboard`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'key-badge-c', 'G': 'key-badge-g', 'D': 'key-badge-d',
      'A': 'key-badge-a', 'E': 'key-badge-e', 'B': 'key-badge-b',
      'F#': 'key-badge-fsharp', 'Db': 'key-badge-db', 'Ab': 'key-badge-ab',
      'Eb': 'key-badge-eb', 'Bb': 'key-badge-bb', 'F': 'key-badge-f'
    };
    const baseKey = key?.replace('m', '').replace('#', '').replace('b', '');
    return keyMap[key] || keyMap[baseKey] || 'bg-primary';
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            Hola, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Bienvenido a tu centro de música de adoración
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/songs?new=true">
            <Button className="gap-2" data-testid="add-song-btn">
              <Music className="w-4 h-4" />
              Nueva canción
            </Button>
          </Link>
          <Link to="/generator">
            <Button variant="outline" className="gap-2" data-testid="generator-btn">
              <Sparkles className="w-4 h-4" />
              Generar Popurrí
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50 hover:shadow-md transition-shadow" data-testid="stat-songs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Canciones</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_songs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Music className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 hover:shadow-md transition-shadow" data-testid="stat-medleys">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Medleys</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_medleys || 0}</p>
              </div>
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <ListMusic className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 hover:shadow-md transition-shadow" data-testid="stat-favorites">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Favoritos</p>
                <p className="text-3xl font-bold mt-1">{stats?.favorites_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 hover:shadow-md transition-shadow" data-testid="stat-my-songs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Mis canciones</p>
                <p className="text-3xl font-bold mt-1">{stats?.my_songs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-chart-3/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-chart-3" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Songs */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="md:col-span-1 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" />
              Acciones rápidas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/generator" className="block">
              <Button variant="outline" className="w-full justify-between h-12">
                Generar Popurrí con IA
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/songs?new=true" className="block">
              <Button variant="outline" className="w-full justify-between h-12">
                Agregar canción
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/singer" className="block">
              <Button variant="outline" className="w-full justify-between h-12 text-accent border-accent/30 hover:bg-accent/10">
                Vista de Cantante
                <Play className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/community" className="block">
              <Button variant="outline" className="w-full justify-between h-12">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Comunidad
                </span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Songs */}
        <Card className="md:col-span-2 border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Canciones recientes</CardTitle>
              <CardDescription>Últimas canciones agregadas</CardDescription>
            </div>
            <Link to="/songs">
              <Button variant="ghost" size="sm" className="gap-1">
                Ver todas
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : stats?.recent_songs?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_songs.map((song, index) => (
                  <Link
                    key={song.song_id}
                    to={`/songs/${song.song_id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                    style={{ animationDelay: `${index * 100}ms` }}
                    data-testid={`recent-song-${song.song_id}`}
                  >
                    {!singerMode && (
                      <div className={`key-badge ${getKeyBadgeClass(song.original_key)}`}>
                        {song.original_key}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{song.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {song.theme || 'General'} • {song.bpm ? (singerMode ? bpmToTempoLabel(song.bpm) : `${song.bpm} BPM`) : 'Sin BPM'}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Music className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No hay canciones aún</p>
                <Link to="/songs?new=true">
                  <Button variant="link" className="mt-2">
                    Agregar primera canción
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
